import { proxy as comlinkProxy } from 'comlink';
import { Dictionary, keyBy } from 'lodash';
import _flatMap from 'lodash/flatMap';
import _keyBy from 'lodash/keyBy';
import _unionBy from 'lodash/unionBy';
import { useQuery, UseQueryOptions, UseQueryResult } from 'react-query';
import { useDispatch } from 'react-redux';
import { Team, User, UserStatus, UserType } from 'api/models';
import {
  MembersAPI,
  MembersResponse,
  MembersV2Response,
} from 'data/members/api';
import { putAll as putAllTeams } from 'stores/teams';
import { putAll as putAllUsers } from 'stores/users';
import { ActiveUsersAndTeams } from 'utils/active-users-and-teams';
import { callWorkerWithCleanup } from 'utils/call-worker-with-cleanup';
import { oneDay, oneHour } from './constants';
import { useHideNonStrategicPartner } from './partner';

export const MEMBERS_V2_QUERY_KEY = ['members-v2'];

export const ALL_MEMBERS_QUERY_KEY = ['all-members'];
export const ALL_MEMBERS_MAP_QUERY_KEY = ['all-members-map'];
type BaseMembersQueryOptions<TData = unknown> = UseQueryOptions<
  MembersResponse,
  unknown,
  TData,
  typeof ALL_MEMBERS_QUERY_KEY
>;

export type BaseMembersV2QueryOptions<TData = unknown> = UseQueryOptions<
  MembersV2Response,
  unknown,
  TData,
  typeof ALL_MEMBERS_QUERY_KEY
>;

export type MembersQueryOptions = BaseMembersQueryOptions<MembersResponse>;
type MembersV2QueryOptions = BaseMembersV2QueryOptions<MembersV2Response>;

export type MembersQueryResult = UseQueryResult<MembersResponse, unknown>;
type MembersV2QueryResult = UseQueryResult<MembersV2Response, unknown>;

export const BaseMembersQueryOptions: Pick<
  BaseMembersQueryOptions,
  'queryKey' | 'queryFn' | 'staleTime'
> = {
  queryKey: ALL_MEMBERS_QUERY_KEY,
  queryFn: () => MembersAPI.findAll(),
  staleTime: oneDay,
};

const BaseMembersV2QueryOptions: Pick<
  BaseMembersV2QueryOptions,
  'queryKey' | 'queryFn' | 'staleTime'
> = {
  queryKey: MEMBERS_V2_QUERY_KEY,
  queryFn: () => MembersAPI.getMembersV2(),
  staleTime: oneHour,
};

export const activeUsersAndTeamsFromIndexedB = new ActiveUsersAndTeams();

// @TODO: This need to be moved as a custom hook and called inside a component.
// @TODO: Remove the react query call and move the new memebers api call inside the worker.
function processMembersDataInWorker(
  documents: {
    users: User[];
    teams: Team[];
  },
  onDataSeededIntoIndexedDB?: () => void,
) {
  const worker = callWorkerWithCleanup();

  worker.workerApi.processMembersData(
    documents,
    comlinkProxy(({ users, teams }) => {
      activeUsersAndTeamsFromIndexedB.setActiveUsersAndTeams = {
        users,
        teams,
      };
      onDataSeededIntoIndexedDB?.();
      // clean up worker after members and teams data inserted
      worker.cleanup();
    }),
  );
}

export const useMembersV2Query = (
  {
    queryKey,
    queryFn,
    staleTime,
    select,
    ...restOptions
  }: MembersV2QueryOptions,
  onDataSeededIntoIndexedDB?: () => void,
): MembersV2QueryResult => {
  const dispatch = useDispatch();
  const {
    isDependentEntitiesLoading,
    isCurrentUserCustomer,
    updateCompanyIfNonStrategic,
  } = useHideNonStrategicPartner();

  return useQuery({
    ...BaseMembersV2QueryOptions,
    enabled: !isDependentEntitiesLoading,
    onSuccess: (data) => {
      const suggestionsData = [...Object.values(data.users)];

      const membersData: Array<User> = suggestionsData.map((user: User) => ({
        ...user,
        userId: user.userId,
      }));

      const users = isCurrentUserCustomer()
        ? membersData.map((member) => ({
            ...member,
            company: updateCompanyIfNonStrategic(member.company),
          }))
        : membersData;
      const teams = Object.values(data.teams);

      // These dispatch methods were moved from app-boot-data.tsx after removing the api call to /members on page load
      dispatch(putAllUsers([]));
      dispatch(putAllTeams(teams ?? []));

      // Handle storing data to indexeddb inside the web worker
      processMembersDataInWorker({ teams, users }, onDataSeededIntoIndexedDB);
    },
    ...restOptions,
    refetchOnWindowFocus: true,
  });
};

export const useMembersQuery = ({
  queryKey,
  queryFn,
  staleTime,
  ...restOptions
}: MembersQueryOptions = {}): MembersQueryResult => {
  return useQuery({
    ...BaseMembersQueryOptions,
    ...restOptions,
  });
};

export const useNativeMembersQuery = ({
  queryKey,
  queryFn,
  staleTime,
  select,
  ...restOptions
}: MembersQueryOptions = {}): MembersQueryResult => {
  return useQuery({
    ...BaseMembersQueryOptions,
    select: ({ users, members, ...rest }) => {
      const result: MembersResponse = {
        ...rest,
        users: (users ?? []).filter(
          ({ userType }) => userType === UserType.Native,
        ),
        members: (members ?? []).filter(
          ({ user }) => user.userType === UserType.Native,
        ),
      };

      return result;
    },
    ...restOptions,
  });
};

export const ALL_USERS_QUERY_KEY = ALL_MEMBERS_QUERY_KEY;
export const useUsersQuery = (): UseQueryResult<User[]> => {
  return useQuery<MembersResponse, unknown, User[], typeof ALL_USERS_QUERY_KEY>(
    {
      ...BaseMembersQueryOptions,
      select: (res) => {
        return res.members.map((member) => member.user);
      },
    },
  );
};

export const useUsersMapQuery = (): UseQueryResult<Map<number, User>> => {
  return useQuery<
    MembersResponse,
    unknown,
    Map<number, User>,
    typeof ALL_MEMBERS_MAP_QUERY_KEY
  >({
    ...BaseMembersQueryOptions,
    select: (res) => {
      return new Map<number, User>(
        res.members.map((member) => [member.user.userId, member.user]),
      );
    },
  });
};

export const useNativeActiveOrInvitedUsersQuery = (): UseQueryResult<
  User[]
> => {
  return useQuery<MembersResponse, unknown, User[], typeof ALL_USERS_QUERY_KEY>(
    {
      ...BaseMembersQueryOptions,
      select: (res) => {
        return res.members
          .map(({ user }) => user)
          .filter(
            ({ userStatus, userType }) =>
              (userStatus === UserStatus.Active ||
                userStatus === UserStatus.Invited) &&
              userType === UserType.Native,
          );
      },
    },
  );
};
export const useActiveOrInvitedNonCustomerUsersQuery = (): UseQueryResult<
  User[]
> => {
  return useQuery<MembersResponse, unknown, User[], typeof ALL_USERS_QUERY_KEY>(
    {
      ...BaseMembersQueryOptions,
      select: (res) => {
        return res.members
          .map(({ user }) => user)
          .filter(
            ({ userStatus, userType }) =>
              (userStatus === UserStatus.Active ||
                userStatus === UserStatus.Invited) &&
              userType !== UserType.External,
          );
      },
    },
  );
};

const USER_LOOKUP_KEY = 'USER_TIMEOFF_LOOKUP';

export const useUserTimeoffLookup = (): UseQueryResult<Dictionary<User>> => {
  const { data: nativeActiveUsers = [] } = useNativeActiveOrInvitedUsersQuery();

  return useQuery(USER_LOOKUP_KEY, () => {
    return keyBy(nativeActiveUsers, 'userId');
  });
};
